import { useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BusyLabel, InlineAlert } from "@/components/admin/feedback";
import { PageFrame } from "@/components/admin/page-frame";
import { PageHeader } from "@/components/admin/page-header";
import { PermissionHint } from "@/components/admin/status";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminApi, apiErrorMessage } from "@/lib/api";
import { can, requiredRole } from "@/lib/permissions";
import { queryKeys, useCurrentIdentityQuery } from "@/lib/queries";

export function CreateCustomerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const identityQuery = useCurrentIdentityQuery();
  const allowed = can(identityQuery.data?.roles, "createRecords");
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const mutation = useMutation({
    mutationFn: adminApi.createCustomer,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.customers });
      navigate("/admin/customers");
    },
    onError: (value) => setError(apiErrorMessage(value)),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) return;
    const form = new FormData(event.currentTarget);
    const name = String(form.get("name") ?? "").trim();
    const reason = String(form.get("reason") ?? "").trim();
    if (!name || !reason) {
      setError("Customer name and reason are required.");
      const firstInvalid = !name
        ? nameRef.current
        : event.currentTarget.elements.namedItem("reason");
      (firstInvalid as HTMLElement | null)?.focus();
      return;
    }
    setError(null);
    mutation.mutate({
      name,
      id: String(form.get("id") ?? "").trim() || undefined,
      reason,
    });
  }

  return (
    <PageFrame size="form">
      <PageHeader
        title={
          <>
            <Users className="size-6 text-muted sm:size-7" aria-hidden="true" />
            {t("customers.form.createTitle", "Create customer")}
          </>
        }
        titleClassName="flex items-center gap-2.5"
        description={t("customers.form.createDesc", "Record the commercial entity that will hold licenses and deployments.")}
        actions={
          <Button variant="ghost" onClick={() => navigate("/admin/customers")}>
            <ArrowLeft className="size-4" aria-hidden="true" />
            {t("common.back", "Back to customers")}
          </Button>
        }
      />
      {!allowed && (
        <PermissionHint
          role={requiredRole("createRecords")}
          action="create customers"
        />
      )}
      <form
        className="rounded-lg border border-line bg-panel p-5 sm:p-7"
        onSubmit={submit}
        noValidate
      >
        <fieldset disabled={!allowed} className="min-w-0 space-y-6">
          <section className="space-y-4">
            <h2 className="font-semibold text-ink">{t("customers.form.identity", "Customer identity")}</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="customer-name">{t("customers.form.name", "Customer / Organization name")}</Label>
                <Input
                  ref={nameRef}
                  id="customer-name"
                  name="name"
                  required
                  aria-invalid={Boolean(error)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer-id">
                  {t("customers.form.externalRef", "External reference ID")}{" "}
                  <span className="font-normal text-muted">(optional)</span>
                </Label>
                <Input id="customer-id" name="id" placeholder="cust_acme" />
              </div>
            </div>
          </section>
          <section className="space-y-2">
            <Label htmlFor="customer-reason">{t("dialogs.reason", "Reason")}</Label>
            <Input
              id="customer-reason"
              name="reason"
              required
              aria-invalid={Boolean(error)}
              aria-describedby={error ? "customer-form-error" : undefined}
              placeholder="Approved pilot customer"
            />
          </section>
          {error && (
            <div id="product-form-error">
              <InlineAlert title="Customer not created" message={error} />
            </div>
          )}
        </fieldset>
        <div className="mt-6 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/customers")}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!allowed || mutation.isPending}
          >
            {mutation.isPending ? (
              <BusyLabel>Creating</BusyLabel>
            ) : (
              t("customers.form.saveCustomer", "Create customer")
            )}
          </Button>
        </div>
      </form>
    </PageFrame>
  );
}
